import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdSense from '../../components/shared/AdSense';
import sportsKeywords from './sportsKeywords';
import SuggestionModal from './SuggestionModal';
import GameCard from './GameCard';
import {
  PlayerImageIcon,
  DrawGameIcon,
  BuySellIcon,
  SoundBoardIcon,
  SellSellIcon,
  QuestionIcon,
  QuizIcon,
  CalendarIcon,
  DilemmaIcon,
  CountryBattleIcon
} from './homePageIcons';

const HomePageBackup = () => {
  const navigate = useNavigate();
  const [suggestionEmail, setSuggestionEmail] = useState('');
  const [suggestionCategory, setSuggestionCategory] = useState('');
  const [suggestionMessage, setSuggestionMessage] = useState('');
  const [suggestionStatus, setSuggestionStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const resetSuggestionForm = useCallback(() => {
    setSuggestionEmail('');
    setSuggestionCategory('');
    setSuggestionMessage('');
    setSuggestionStatus('');
  }, []);

  const openSuggestionModal = useCallback(() => {
    setSuggestionStatus('');
    setShowSuggestionForm(true);
  }, []);

  const closeSuggestionModal = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    setShowSuggestionForm(false);
    resetSuggestionForm();
  }, [isSubmitting, resetSuggestionForm]);
  
  // Adstra settings state
  const [adstraSettings, setAdstraSettings] = useState({
    adstraLink: '',
    impressionsRequired: 0,
    isEnabled: false
  });
  const [adstraClickCount, setAdstraClickCount] = useState(() => {
    try {
      const saved = sessionStorage.getItem('adstra_click_count');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });
  
  // Fetch Adstra settings on mount
  useEffect(() => {
    const fetchAdstraSettings = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/adstra/settings`);
        if (response.ok) {
          const data = await response.json();
          setAdstraSettings(data);
        }
      } catch (error) {
        console.error('Error fetching Adstra settings:', error);
      }
    };
    
    fetchAdstraSettings();
  }, [apiUrl]);

  useEffect(() => {
    if (!showSuggestionForm) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showSuggestionForm]);
  
  useEffect(() => {
    const prefetchSportsData = async () => {
      const today = new Date();
      const datesToFetch = [];

      for (let i = 0; i < 8; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        datesToFetch.push(date);
      }

      const fetchPromises = datesToFetch.map(date => 
        prefetchDataForDate(date).catch(err => {
          console.error(`Failed to prefetch data for ${date.toDateString()}:`, err);
          return null;
        })
      );

      await Promise.allSettled(fetchPromises);
      console.log('✅ Successfully prefetched sports data for 8 days');
    };

    const prefetchTimer = setTimeout(() => {
      prefetchSportsData();
    }, 1000);

    return () => clearTimeout(prefetchTimer);
  }, []);

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.screen.width <= 768;
      
      if (isMobile) {
        viewport.setAttribute('content', 'width=650, initial-scale=0.6, minimum-scale=0.4, maximum-scale=5.0, user-scalable=yes');
      } else {
        viewport.setAttribute('content', 'width=900, initial-scale=0.42, minimum-scale=0.3, maximum-scale=5.0, user-scalable=yes');
      }
    }
    return () => {
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      }
    };
  }, []);

  const prefetchDataForDate = async (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const cacheKey = `sports_ultra_${month}_${day}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const cachedData = JSON.parse(cached);
        const cacheAge = Date.now() - cachedData.timestamp;
        if (cacheAge < 3600000) {
          console.log(`✓ Data already cached for ${month}/${day}`);
          return;
        }
      }
    } catch (error) {
      console.error('Cache check error:', error);
    }

    try {
      console.log(`⏳ Prefetching data for ${month}/${day}...`);
      
      const apiPromises = [
        fetchWikipediaAllEndpoints(month, day),
        fetchZenQuotesData(month, day),
        fetchWikipediaEnhanced(month, day),
        fetchMuffinLabsData(month, day),
        fetchDayInHistoryData(month, day)
      ];

      const results = await Promise.allSettled(apiPromises);
      
      const allData = results
        .filter(result => result.status === 'fulfilled' && result.value)
        .map(result => result.value);

      const mergedData = mergeMultipleApiData(allData);
      
      if (mergedData.events.length > 0 || mergedData.births.length > 0 || mergedData.deaths.length > 0) {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: mergedData,
          timestamp: Date.now()
        }));
        console.log(`✓ Cached ${mergedData.events.length} events, ${mergedData.births.length} births, ${mergedData.deaths.length} deaths for ${month}/${day}`);
      }
    } catch (error) {
      console.error(`Error prefetching data for ${month}/${day}:`, error);
    }
  };

  const fetchWikipediaEnhanced = async (month, day) => {
    try {
      const response = await fetch(`https://en.wikipedia.org/api/rest_v1/feed/onthisday/all/${month}/${day}`);
      if (!response.ok) return null;
      const data = await response.json();

      const formattedData = {
        events: (data.events || []).map(item => ({
          text: item.text,
          year: item.year || extractYear(item.text),
          pages: item.pages || []
        })),
        births: (data.births || []).map(item => ({
          text: item.text,
          year: item.year || extractYear(item.text),
          pages: item.pages || []
        })),
        deaths: (data.deaths || []).map(item => ({
          text: item.text,
          year: item.year || extractYear(item.text),
          pages: item.pages || []
        }))
      };

      return filterSportsContent(formattedData);
    } catch (error) {
      return null;
    }
  };

  const fetchWikipediaAllEndpoints = async (month, day) => {
    const endpoints = [
      `https://api.wikimedia.org/feed/v1/wikipedia/en/onthisday/all/${month}/${day}`,
      `https://api.wikimedia.org/feed/v1/wikipedia/en/onthisday/events/${month}/${day}`,
      `https://api.wikimedia.org/feed/v1/wikipedia/en/onthisday/births/${month}/${day}`,
      `https://api.wikimedia.org/feed/v1/wikipedia/en/onthisday/deaths/${month}/${day}`,
      `https://api.wikimedia.org/feed/v1/wikipedia/en/onthisday/selected/${month}/${day}`
    ];

    const headers = { 'Api-User-Agent': 'SportsHistoryApp/3.0 (Educational Purpose)' };

    try {
      const responses = await Promise.all(
        endpoints.map(url => 
          fetch(url, { headers })
            .then(res => res.ok ? res.json() : null)
            .catch(() => null)
        )
      );

      const combinedData = { events: [], births: [], deaths: [] };

      responses.forEach(data => {
        if (data) {
          if (data.events) combinedData.events.push(...data.events);
          if (data.selected) combinedData.events.push(...data.selected);
          if (data.births) combinedData.births.push(...data.births);
          if (data.deaths) combinedData.deaths.push(...data.deaths);
        }
      });

      return filterSportsContent(combinedData);
    } catch (error) {
      return null;
    }
  };

  const fetchZenQuotesData = async (month, day) => {
    try {
      const response = await fetch(`https://today.zenquotes.io/api/${parseInt(month)}/${parseInt(day)}`);
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.data) return null;

      const formattedData = {
        events: (data.data.Events || []).map(item => ({
          text: item.text,
          year: extractYear(item.text),
          pages: extractWikiLinks(item.html)
        })),
        births: (data.data.Births || []).map(item => ({
          text: item.text,
          year: extractYear(item.text),
          pages: extractWikiLinks(item.html)
        })),
        deaths: (data.data.Deaths || []).map(item => ({
          text: item.text,
          year: extractYear(item.text),
          pages: extractWikiLinks(item.html)
        }))
      };

      return filterSportsContent(formattedData);
    } catch (error) {
      return null;
    }
  };

  const fetchMuffinLabsData = async (month, day) => {
    try {
      const response = await fetch(`https://history.muffinlabs.com/date/${month}/${day}`);
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.data) return null;

      const formattedData = {
        events: (data.data.Events || []).map(item => ({
          text: item.text || item.year + ' - ' + item.html,
          year: parseInt(item.year) || extractYear(item.text),
          pages: item.links || []
        })),
        births: (data.data.Births || []).map(item => ({
          text: item.text || item.year + ' - ' + item.html,
          year: parseInt(item.year) || extractYear(item.text),
          pages: item.links || []
        })),
        deaths: (data.data.Deaths || []).map(item => ({
          text: item.text || item.year + ' - ' + item.html,
          year: parseInt(item.year) || extractYear(item.text),
          pages: item.links || []
        }))
      };

      return filterSportsContent(formattedData);
    } catch (error) {
      return null;
    }
  };

  const fetchDayInHistoryData = async (month, day) => {
    try {
      const response = await fetch(`https://dayinhistory.dev/api/events/${month}/${day}`);
      if (!response.ok) return null;
      const data = await response.json();
      
      const formattedData = {
        events: (data.events || []).map(item => ({
          text: item.description || item.text || item.event,
          year: item.year || extractYear(item.description || item.text),
          pages: []
        })),
        births: (data.births || []).map(item => ({
          text: item.description || item.text || item.name,
          year: item.year || extractYear(item.description || item.text),
          pages: []
        })),
        deaths: (data.deaths || []).map(item => ({
          text: item.description || item.text || item.name,
          year: item.year || extractYear(item.description || item.text),
          pages: []
        }))
      };

      return filterSportsContent(formattedData);
    } catch (error) {
      return null;
    }
  };

  const extractYear = (text) => {
    if (!text) return new Date().getFullYear();
    const yearMatch = text.match(/(\d{4})/);
    return yearMatch ? parseInt(yearMatch[1]) : new Date().getFullYear();
  };

  const extractWikiLinks = (html) => {
    if (!html) return [];
    const linkRegex = /href="([^"]+)">([^<]+)</g;
    const links = [];
    let match;
    
    while ((match = linkRegex.exec(html)) !== null && links.length < 2) {
      const url = match[1];
      if (url.includes('wikipedia.org/wiki/')) {
        const wikiTitle = url.split('/wiki/')[1];
        links.push({ title: wikiTitle });
      }
    }
    return links;
  };

  const filterSportsContent = (data) => {
    const isSportsRelated = (text) => {
      if (!text) return false;
      const lowerText = text.toLowerCase();
      return sportsKeywords.some(keyword => lowerText.includes(keyword.toLowerCase()));
    };

    const filterAndEnhance = (items) => {
      return items
        .filter(item => item.text && isSportsRelated(item.text))
        .map(item => ({
          ...item,
          sport: detectSport(item.text)
        }))
        .sort((a, b) => (b.year || 0) - (a.year || 0));
    };

    return {
      events: filterAndEnhance(data.events || []),
      births: filterAndEnhance(data.births || []),
      deaths: filterAndEnhance(data.deaths || [])
    };
  };

  const detectSport = (text) => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('football') || lowerText.includes('soccer') || lowerText.includes('fifa')) return 'football';
    if (lowerText.includes('tennis') || lowerText.includes('wimbledon') || lowerText.includes('us open')) return 'tennis';
    if (lowerText.includes('cricket') || lowerText.includes('test match') || lowerText.includes('ipl')) return 'cricket';
    if (lowerText.includes('basketball') || lowerText.includes('nba')) return 'basketball';
    if (lowerText.includes('baseball') || lowerText.includes('mlb')) return 'baseball';
    if (lowerText.includes('golf')) return 'golf';
    if (lowerText.includes('rugby')) return 'rugby';
    if (lowerText.includes('hockey')) return 'hockey';
    return 'other';
  };

  const mergeMultipleApiData = (dataArray) => {
    const merged = { events: [], births: [], deaths: [] };
    dataArray.forEach(data => {
      if (data) {
        merged.events.push(...(data.events || []));
        merged.births.push(...(data.births || []));
        merged.deaths.push(...(data.deaths || []));
      }
    });

    return {
      events: removeDuplicatesAdvanced(merged.events),
      births: removeDuplicatesAdvanced(merged.births),
      deaths: removeDuplicatesAdvanced(merged.deaths)
    };
  };

  const removeDuplicatesAdvanced = (arr) => {
    const unique = [];
    for (const item of arr) {
      const isDuplicate = unique.some(existing => {
        if (existing.year === item.year) {
          return similarity(existing.text, item.text) > 0.70;
        }
        return false;
      });
      if (!isDuplicate) {
        unique.push(item);
      }
    }
    return unique.sort((a, b) => (b.year || 0) - (a.year || 0));
  };

  const similarity = (s1, s2) => {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    if (longer.length === 0) return 1.0;
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const levenshteinDistance = (s1, s2) => {
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  useEffect(() => {
    const handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const [imageErrors, setImageErrors] = useState({
    matchVote: false,
    sportsGames: false,
    playerImage: false,
    drawGame: false,
    buySell: false,
    soundBoard: false
  });

  const handleImageError = useCallback((imageType) => {
    setImageErrors(prev => ({
      ...prev,
      [imageType]: true
    }));
  }, []);

  const MatchVoteIcon = useCallback(() => {
    if (!imageErrors.matchVote) {
      return (
        <img
          src={process.env.PUBLIC_URL + "/images/vote.png"}
          alt="Match Vote"
          className="object-cover rounded-lg border-white"
          style={{ width: 'clamp(40px, 5vw, 64px)', height: 'clamp(40px, 5vw, 64px)', filter: 'brightness(0) saturate(100%) invert(100%) contrast(100%)' }}
          onError={() => handleImageError('matchVote')}
        />
      );
    }
    return <span style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>🗳️</span>;
  }, [imageErrors.matchVote, handleImageError]);

  const SportsGamesIcon = useCallback(() => {
    if (!imageErrors.sportsGames) {
      return (
        <img
          src={process.env.PUBLIC_URL + "/images/games.png"}
          alt="Sports Games"
          className="object-contain rounded-lg"
          style={{ width: 'clamp(40px, 5vw, 64px)', height: 'clamp(40px, 5vw, 64px)' }}
          onError={() => handleImageError('sportsGames')}
        />
      );
    }
    return <span style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>🎮</span>;
  }, [imageErrors.sportsGames, handleImageError]);

  const menuItems = [
    { 
      title: (
        <span className="block text-left md:whitespace-nowrap">
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>MATCH </span>
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>VOTE</span>
        </span>
      ), 
      description: <span>Vote on upcoming<br className="md:hidden"/> matches</span>,
      icon: <MatchVoteIcon />, 
      bgColor: 'bg-green-500', 
      textColor: 'text-white', 
      route: '/match-vote',
      cols: 'col-span-2',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: (
        <span className="block text-left md:whitespace-nowrap">
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>SPORTS </span>
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>GAMES</span>
        </span>
      ), 
      description: <span>Play 1000+ fun<br className="md:hidden"/> sports games</span>,
      icon: <SportsGamesIcon />, 
      bgColor: 'bg-yellow-400', 
      textColor: 'text-gray-900', 
      route: '/sports-games',
      cols: 'col-span-2',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: (
        <span className="block text-left md:whitespace-nowrap">
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>PLAYER </span>
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>REVIEW</span>
        </span>
      ), 
      description: <span>Rate and review<br className="md:hidden"/> players</span>,
      icon: <PlayerImageIcon />, 
      bgColor: 'bg-blue-500', 
      textColor: 'text-white', 
      route: '/player-review',
      cols: 'col-span-2',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: (
        <span className="text-left block md:whitespace-nowrap">
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>DRAW </span>
          <span className="block md:inline font-black leading-tight uppercase whitespace-nowrap" style={{ fontSize: 'clamp(0.75rem, 1.3vw, 1.25rem)' }}>THE LEGEND</span>
        </span>
      ),
      description: <span>Sketch sports icons<br className="md:hidden"/> from memory</span>,
      icon: <DrawGameIcon />, 
      bgColor: 'bg-orange-500', 
      textColor: 'text-white', 
      route: '/draw-game',
      cols: 'col-span-2',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: (
        <span className="text-left block md:whitespace-nowrap">
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.875rem)' }}>Sports </span>
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.875rem)' }}>market</span>
        </span>
      ), 
      description: 'Spend virtual money on sports items',
      icon: <BuySellIcon />, 
      bgColor: 'bg-red-500', 
      textColor: 'text-white', 
      route: '/buy-sell',
      cols: 'col-span-2',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: (
        <span className="block text-left md:whitespace-nowrap">
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>SPORTS </span>
          <span className="block md:inline font-black leading-tight uppercase whitespace-nowrap" style={{ fontSize: 'clamp(0.75rem, 1.3vw, 1.25rem)' }}>CRAFT LAB</span>
        </span>
      ),
      description: 'Combine items to make sports gear',
      icon: <SoundBoardIcon />, 
      bgColor: 'bg-indigo-500', 
      textColor: 'text-white', 
      route: '/sports-craft',
      cols: 'col-span-2',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: <span className="uppercase tracking-wide whitespace-nowrap" style={{ fontSize: 'clamp(0.75rem, 1.1vw, 1rem)' }}>COUNTRY BATTLE <span className="text-yellow-600">ARENA</span></span>, 
      description: 'Battle for points, flag vs flag',
      icon: <CountryBattleIcon />, 
      bgColor: 'bg-gradient-to-b from-orange-500 via-yellow-400 to-green-600', 
      textColor: 'text-white', 
      route: '/country-battle',
      cols: 'col-span-3',
      isFeature: true
    },
    { 
      title: (
        <span className="block text-left">
          <span className="block font-black leading-none uppercase whitespace-nowrap drop-shadow-md" style={{ fontSize: 'clamp(0.7rem, 1.5vw, 1.875rem)' }}>SELL SELL SELL</span>
        </span>
      ), 
      description: 'Print and sell virtual sports money',
      icon: <SellSellIcon />, 
      bgColor: 'bg-gradient-to-br from-red-600 via-red-500 to-red-700', 
      textColor: 'text-white', 
      route: '/sell-sell-sell',
      cols: 'col-span-3',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: <span className="font-black leading-tight uppercase tracking-wide whitespace-nowrap" style={{ fontSize: 'clamp(0.875rem, 1.3vw, 1.25rem)' }}>DAILY SPORTS QUIZ</span>, 
      description: 'Test your sports knowledge',
      icon: <QuizIcon />, 
      bgColor: 'bg-gradient-to-b from-green-600 to-green-700', 
      textColor: 'text-white', 
      route: '/daily-quiz',
      cols: 'col-span-3',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: (
        <span className="block text-left md:whitespace-nowrap">
          <span className="block md:inline font-black leading-tight uppercase" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.875rem)' }}>MYSTERY </span>
          <span className="block md:inline font-black leading-tight uppercase whitespace-nowrap" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.5rem)' }}>SPORTS LEGEND</span>
        </span>
      ), 
      description: 'Guess the mystery player',
      icon: <QuestionIcon />, 
      bgColor: 'bg-blue-700', 
      textColor: 'text-white', 
      route: '/mystery-sports-legend',
      cols: 'col-span-3',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: (
        <span className="uppercase tracking-wide whitespace-nowrap" style={{ fontSize: 'clamp(0.875rem, 1.3vw, 1.25rem)' }}>
          <span className="font-black text-teal-950">ON THIS DAY</span>
          <span className="font-bold text-teal-950/70 ml-1">IN SPORTS</span>
        </span>
      ), 
      description: 'Relive historic sports moments',
      icon: <CalendarIcon />, 
      bgColor: 'bg-cyan-300', 
      textColor: 'text-teal-950', 
      route: '/on-this-day',
      cols: 'col-span-3',
      isFeature: true,
      layout: 'header-title'
    },
    { 
      title: <span className="font-black leading-tight uppercase tracking-wide whitespace-nowrap drop-shadow-md" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.875rem)' }}>FUN DILEMMA</span>, 
      description: '5 daily dilemmas • Build your streak!',
      icon: <DilemmaIcon />, 
      bgColor: 'bg-gradient-to-r from-pink-500 to-rose-500', 
      textColor: 'text-white', 
      route: '/fun-dilemma',
      cols: 'col-span-3',
      isFeature: true,
      layout: 'header-title'
    }
  ];

  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    
    if (!suggestionEmail || !suggestionCategory || !suggestionMessage) {
      setSuggestionStatus('error');
      setTimeout(() => setSuggestionStatus(''), 3000);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(suggestionEmail)) {
      setSuggestionStatus('invalid-email');
      setTimeout(() => setSuggestionStatus(''), 3000);
      return;
    }

    setIsSubmitting(true);
    setSuggestionStatus('sending');

    try {
      const response = await fetch(`${apiUrl}/api/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: suggestionEmail,
          category: suggestionCategory,
          message: suggestionMessage
        })
      });

      await response.json();

      if (response.ok) {
        setSuggestionStatus('success');
        resetSuggestionForm();
        
        setTimeout(() => {
          setShowSuggestionForm(false);
          setSuggestionStatus('');
        }, 2000);
      } else {
        setSuggestionStatus('failed');
        setTimeout(() => setSuggestionStatus(''), 3000);
      }
    } catch (error) {
      console.error('Error sending suggestion:', error);
      setSuggestionStatus('network-error');
      setTimeout(() => setSuggestionStatus(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardClick = useCallback((item) => {
    if (!item.route) return;
    
    if (adstraSettings.isEnabled && adstraSettings.adstraLink && adstraSettings.impressionsRequired > 0) {
      if (adstraClickCount < adstraSettings.impressionsRequired) {
        const newCount = adstraClickCount + 1;
        setAdstraClickCount(newCount);
        
        try {
          sessionStorage.setItem('adstra_click_count', newCount.toString());
          sessionStorage.setItem('adstra_pending_route', item.route);
        } catch (e) {
          console.error('Error saving to sessionStorage:', e);
        }
        
        fetch(`${apiUrl}/api/adstra/impression`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }).catch(err => console.error('Error tracking impression:', err));
        
        if (newCount >= adstraSettings.impressionsRequired) {
          fetch(`${apiUrl}/api/adstra/completed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          }).catch(err => console.error('Error tracking completion:', err));
        }
        
        window.location.href = adstraSettings.adstraLink;
        return;
      }
    }
    
    navigate(item.route);
  }, [navigate, adstraSettings, adstraClickCount, apiUrl]);

  const onKeyActivate = useCallback((e, item) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(item);
    }
  }, [handleCardClick]);

  return (
    <div
      className="min-h-screen bg-blue-500"
      style={{ 
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      {showSuggestionForm && (
        <SuggestionModal
          suggestionEmail={suggestionEmail}
          setSuggestionEmail={setSuggestionEmail}
          suggestionCategory={suggestionCategory}
          setSuggestionCategory={setSuggestionCategory}
          suggestionMessage={suggestionMessage}
          setSuggestionMessage={setSuggestionMessage}
          suggestionStatus={suggestionStatus}
          isSubmitting={isSubmitting}
          onClose={closeSuggestionModal}
          onSubmit={handleSuggestionSubmit}
        />
      )}

      <div className="w-full max-w-7xl mx-auto px-4 py-6">
        <header className="text-center mb-8">
          <h1 className="font-bold text-white mb-2 tracking-wide drop-shadow-md" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
            Play the World of Sports
          </h1>
          <p className="text-white/90 font-medium tracking-wide" style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>
            Play • Vote • Explore • Fun with Sports.
          </p>
        </header>

        <AdSense slot="homePageTop" className="mb-6" />

        <section className="pb-12">
          <div 
            className="rounded-3xl p-4"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #F0F9FF 40%, #7DD3FC 75%, #38BDF8 100%)',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1), 0 2px 5px rgba(0, 0, 0, 0.05)'
            }}
          >
            <div className="grid gap-5 grid-cols-6">
              {menuItems.map((item, index) => (
                <GameCard
                  key={index}
                  item={item}
                  onClick={() => handleCardClick(item)}
                  onKeyDown={(e) => onKeyActivate(e, item)}
                />
              ))}
            </div>

          <AdSense slot="homePageBottom" className="mt-6 mb-6" />

          <div className="mt-6 pt-4 border-t border-white/10">
          <div className="rounded-[24px] border border-white/20 p-6 shadow-[0_18px_45px_rgba(36,99,160,0.18)] relative overflow-hidden bg-[rgba(240,249,255,0.4)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.15),transparent_28%),radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.08),transparent_18%),radial-gradient(circle_at_80%_72%,rgba(148,163,184,0.16),transparent_20%)]"></div>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-orange-300/55 to-transparent"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-72 h-[2px] bg-gradient-to-r from-transparent via-amber-200/90 to-transparent blur-[1px]"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-[3px] bg-gradient-to-r from-transparent via-white to-transparent blur-[1px]"></div>
            <div className="absolute top-[-2px] left-1/2 -translate-x-1/2 w-8 h-[4px] bg-white shadow-[0_0_15px_#fff,0_0_5px_#fbbf24] rounded-full"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-orange-400/10 blur-2xl rounded-full"></div>

            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute top-10 left-20 w-1 h-1 bg-white rounded-full animate-pulse"></div>
              <div className="absolute top-40 right-32 w-0.5 h-0.5 bg-white rounded-full animate-pulse delay-75"></div>
              <div className="absolute bottom-20 left-1/2 w-1 h-1 bg-white rounded-full animate-pulse delay-150"></div>
            </div>

            <div className="relative z-10">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0 -translate-y-0.5">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3H19V9C19 12.866 15.866 16 12 16C8.13401 16 5 12.866 5 9V3Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 16V20" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 21H17" stroke="#EAB308" strokeWidth="2.5" strokeLinecap="round"/>
                      <path d="M5 5H3V7C3 8.10457 3.89543 9 5 9V5Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19 5H21V7C21 8.10457 20.1046 9 19 9V5Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 className="font-bold text-slate-900 tracking-tight leading-none" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
                    Coming Soon
                  </h2>
                </div>
                <p className="text-slate-600 font-medium leading-none" style={{ fontSize: 'clamp(0.875rem, 1.2vw, 1rem)' }}>(Upcoming features)</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col" style={{ minHeight: 'clamp(140px, 14vw, 190px)' }}>
                  <div className="flex items-center gap-3 px-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)', height: 'clamp(80px, 9vw, 120px)' }}>
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0"></div>
                    <span className="flex-shrink-0" style={{ fontSize: 'clamp(2rem, 2.7vw, 3rem)', lineHeight: 1 }}>🎮</span>
                    <div>
                      <div className="font-black leading-tight uppercase tracking-wide text-white drop-shadow-sm whitespace-nowrap" style={{ fontSize: 'clamp(0.75rem, 1.15vw, 1.05rem)' }}>FANTASY MANAGER</div>
                    </div>
                    <span className="absolute top-2 right-2 text-[10px] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Soon</span>
                  </div>
                  <div className="flex-1 bg-white px-3 py-2 flex flex-col justify-center">
                    <div className="text-gray-600 font-semibold leading-snug" style={{ fontSize: 'clamp(0.7rem, 1vw, 1rem)' }}>Build &amp; manage your dream sports team</div>
                  </div>
                </div>

                <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col" style={{ minHeight: 'clamp(140px, 14vw, 190px)' }}>
                  <div className="flex items-center gap-3 px-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)', height: 'clamp(80px, 9vw, 120px)' }}>
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0"></div>
                    <span className="flex-shrink-0" style={{ fontSize: 'clamp(2rem, 2.7vw, 3rem)', lineHeight: 1 }}>⏳</span>
                    <div>
                      <div className="font-black leading-tight uppercase tracking-wide text-white drop-shadow-sm whitespace-nowrap" style={{ fontSize: 'clamp(0.75rem, 1.15vw, 1.05rem)' }}>SPORTS TIMELINE</div>
                    </div>
                    <span className="absolute top-2 right-2 text-[10px] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Soon</span>
                  </div>
                  <div className="flex-1 bg-white px-3 py-2 flex flex-col justify-center">
                    <div className="text-gray-600 font-semibold leading-snug" style={{ fontSize: 'clamp(0.7rem, 1vw, 1rem)' }}>Explore key moments in sports history</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col" style={{ minHeight: 'clamp(140px, 14vw, 190px)' }}>
                  <div className="flex items-center gap-3 px-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', height: 'clamp(80px, 9vw, 120px)' }}>
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0"></div>
                    <span className="flex-shrink-0" style={{ fontSize: 'clamp(2rem, 2.7vw, 3rem)', lineHeight: 1 }}>🏟️</span>
                    <div>
                      <div className="font-black leading-tight uppercase tracking-wide text-white drop-shadow-sm whitespace-nowrap" style={{ fontSize: 'clamp(0.75rem, 1.15vw, 1.05rem)' }}>STADIUM BUILDER</div>
                    </div>
                    <span className="absolute top-2 right-2 text-[10px] font-bold bg-white/20 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Soon</span>
                  </div>
                  <div className="flex-1 bg-white px-3 py-2 flex flex-col justify-center">
                    <div className="text-gray-600 font-semibold leading-snug" style={{ fontSize: 'clamp(0.7rem, 1vw, 1rem)' }}>Design &amp; customize your dream arena</div>
                  </div>
                </div>

                <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col relative" style={{ minHeight: 'clamp(140px, 14vw, 190px)', background: 'linear-gradient(135deg, rgba(240,249,255,0.72) 0%, rgba(186,230,253,0.82) 55%, rgba(125,211,252,0.92) 100%)' }}>
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <span className="absolute top-3 left-4 text-sky-700/30 text-xs animate-pulse">✦</span>
                    <span className="absolute top-6 right-8 text-sky-800/25 text-[10px] animate-pulse delay-75">✦</span>
                    <span className="absolute top-10 left-10 text-sky-700/20 text-[8px] animate-pulse delay-150">✦</span>
                    <span className="absolute bottom-10 right-4 text-sky-800/30 text-xs animate-pulse delay-200">✦</span>
                    <span className="absolute bottom-5 left-6 text-sky-700/20 text-[10px] animate-pulse delay-300">✦</span>
                    <span className="absolute top-4 left-1/2 text-sky-900/15 text-[8px] animate-pulse delay-100">✦</span>
                    <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-sky-700/20 to-transparent"></div>
                  </div>
                  <div className="relative z-10 flex-1 flex flex-col items-center justify-center gap-2 p-4">
                    <div style={{ fontSize: 'clamp(1.8rem, 2.5vw, 3rem)', lineHeight: 1 }}>✨</div>
                    <div className="font-black text-slate-900 text-center leading-tight" style={{ fontSize: 'clamp(0.85rem, 1.3vw, 1.2rem)' }}>...and many<br/>more!</div>
                    <div className="text-slate-700 font-medium text-center leading-snug" style={{ fontSize: 'clamp(0.65rem, 0.9vw, 0.85rem)' }}>More features coming your way</div>
                  </div>
                </div>
              </div>

              <div
                className="mb-5 relative overflow-hidden max-w-[440px] mx-auto rounded-[20px]"
                style={{
                  background: 'linear-gradient(135deg, rgba(23,49,118,0.6) 0%, rgba(56,96,194,0.46) 46%, rgba(142,84,233,0.38) 100%)',
                  border: '1.5px solid rgba(255,255,255,0.8)',
                  boxShadow: '0 8px 18px rgba(59,130,246,0.14), inset 0 1px 0 rgba(255,255,255,0.22), 0 0 14px rgba(168,85,247,0.08)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_26%,rgba(255,255,255,0.16),transparent_14%),radial-gradient(circle_at_80%_18%,rgba(250,204,21,0.22),transparent_16%),radial-gradient(circle_at_88%_78%,rgba(217,70,239,0.14),transparent_16%),radial-gradient(circle_at_34%_84%,rgba(96,165,250,0.14),transparent_18%)] pointer-events-none"></div>
                <div className="absolute inset-[1px] rounded-[18px] border border-white/10 pointer-events-none"></div>
                <div className="absolute top-[1px] left-6 right-6 h-px bg-gradient-to-r from-transparent via-white/55 to-transparent pointer-events-none"></div>
                <div className="absolute top-3 left-6 text-white/26 text-[8px] animate-pulse pointer-events-none">✦</div>
                <div className="absolute top-2 right-24 text-amber-200/34 text-[7px] animate-pulse delay-150 pointer-events-none">✦</div>
                <div className="absolute bottom-2 right-36 text-sky-100/22 text-[7px] animate-pulse delay-75 pointer-events-none">✦</div>
                <div className="relative z-10 flex flex-col sm:flex-row sm:items-end gap-2.5 sm:gap-3.5 px-5 sm:px-6 py-5 sm:py-5.5">
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-white/92 leading-tight"
                      style={{
                        fontSize: 'clamp(0.7rem, 0.82vw, 0.76rem)',
                        textShadow: '0 1px 2px rgba(15,23,42,0.42)'
                      }}
                    >
                      Have an idea for the next category?
                    </p>
                    <p
                      className="text-white font-medium leading-tight mt-0.5"
                      style={{
                        fontSize: 'clamp(0.84rem, 1vw, 0.94rem)',
                        textShadow: '0 1px 2px rgba(15,23,42,0.44)'
                      }}
                    >
                      Suggest a New Category.
                    </p>
                  </div>
                  <button
                    onClick={openSuggestionModal}
                    className="flex-shrink-0 inline-flex items-center justify-center gap-1 font-bold text-white transition-all hover:scale-[1.03] rounded-full leading-none self-start sm:self-end mt-0 sm:mt-0 sm:translate-y-1"
                    style={{
                      minWidth: 'clamp(122px, 9.6vw, 132px)',
                      padding: '9px 13px',
                      fontSize: 'clamp(0.54rem, 0.58vw, 0.58rem)',
                      background: 'linear-gradient(180deg, #4867f0 0%, #3557d9 58%, #2944b1 100%)',
                      border: '1px solid rgba(255,255,255,0.16)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -1px 0 rgba(15,23,42,0.22), 0 4px 12px rgba(37,99,235,0.18)'
                    }}
                  >
                    <span>💡 :</span>
                    <span>Send Suggestion</span>
                    <span>💡</span>
                  </button>
                </div>
              </div>

              <div className="text-center space-y-2 border-t border-white/10 pt-4">
                <p className="text-slate-700 font-medium" style={{ fontSize: 'clamp(1rem, 1.5vw, 1.25rem)' }}>
                  Made with <span className="text-red-500" style={{ fontSize: 'clamp(1.25rem, 1.8vw, 1.5rem)' }}>❤️</span> for the World of Sports
                </p>
                <p className="text-slate-600" style={{ fontSize: 'clamp(0.875rem, 1.2vw, 1rem)' }}>
                  Join 1M+ fans discovering fun sports facts every day
                </p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>
    </div>
  </div>
);
};

export default HomePageBackup;
